/**
 * Lens GraphQL Operations
 *
 * Query and TypeScript types for lens resolution.
 */

// ===== TypeScript Types =====

export interface ResolvedLens {
  templateId: string;
  displayName: string;
  landingRoute: string;
  defaultScopeId: string;
  scopeOrder: string[];
}

export interface GetMyLensResponse {
  myLens: ResolvedLens;
}

// ===== Queries =====

export const GET_MY_LENS = `
  query GetMyLens {
    myLens {
      templateId
      displayName
      landingRoute
      defaultScopeId
      scopeOrder
    }
  }
`;

// ===== The frame (ARCH-NOTE-025, CDEV-2149) =====

/** What sort of thing an association's target is; it decides which capability reaches it. */
export type AssociationKind = 'WORLD' | 'WORK_PROJECT' | 'COMMONS' | 'COLLECTION' | 'LOOM_DOCUMENT' | 'ORG';

/** How an association came to be (FRM-3). Navigation is never one. */
export type AssociationProvenance = 'INVITATION' | 'DECLARATION' | 'ACCEPTED_SUGGESTION';

/** FRAME when a grant reaches the target, WANTED (awaiting access, no content) when none does. */
export type FrameRegion = 'FRAME' | 'WANTED';

/** One capability the caller holds, with the scope of the grant that confers it (null: global). */
export interface HeldGrant {
  capability: string;
  verb: string;
  scope: string | null;
  grant: string;
  role: string | null;
}

/** An edge in the caller's own PDOM from the self to an external IRI. */
export interface Association {
  iri: string;
  target: string;
  kind: AssociationKind;
  provenance: AssociationProvenance;
  signer: string;
  statement: string | null;
  evidence: string | null;
  declaredAt: string;
}

/** One association placed in the caller's frame, with the reason (FRM-9). */
export interface FrameEntry {
  association: Association;
  region: FrameRegion;
  capability: string;
  verb: string;
  grant: HeldGrant | null;
}

/** The caller's own invitation record, as invitee (FRM-5). */
export interface Invitation {
  iri: string;
  inviter: string;
  invitee: string;
  locus: string;
  kind: AssociationKind;
  firstReading: string | null;
  grants: string[];
  invitedAt: string;
}

export interface GetMyGrantsResponse {
  myGrants: HeldGrant[];
}

export interface GetMyFrameResponse {
  myFrame: FrameEntry[];
}

export interface GetMyInvitationsResponse {
  myInvitations: Invitation[];
}

export interface GetMyAssociationsResponse {
  myAssociations: Association[];
}

const HELD_GRANT_FIELDS = `
  capability
  verb
  scope
  grant
  role
`;

const ASSOCIATION_FIELDS = `
  iri
  target
  kind
  provenance
  signer
  statement
  evidence
  declaredAt
`;

/** The caller's grants, scope kept (FRM-1). */
export const GET_MY_GRANTS = `
  query GetMyGrants {
    myGrants {
      ${HELD_GRANT_FIELDS}
    }
  }
`;

/** The caller's frame: each association placed FRAME or WANTED, with the grant that reaches it. */
export const GET_MY_FRAME = `
  query GetMyFrame {
    myFrame {
      association {
        ${ASSOCIATION_FIELDS}
      }
      region
      capability
      verb
      grant {
        ${HELD_GRANT_FIELDS}
      }
    }
  }
`;

/**
 * What was offered into the caller's PDOM (CDEV-2298), for Arrival's intro.
 * Under a person preview the server answers only what the previewer offered.
 */
export const GET_ARRIVAL_OFFERS = `
  query GetArrivalOffers {
    sources(limit: 200) {
      source {
        id
        sourceKind
        sourceLabel
        biographyText
        uri
        publication {
          year
          workType
          venue
          authors
        }
        credit {
          department
          roles
          endYear
          episodeCount
          creditedAs
        }
        record {
          status
          offer {
            offeredBy
            offeredAt
            offeredFrom
            acceptedAt
          }
        }
      }
      yields {
        claimId
        statement
        claimStatus
        locator
        group
        acceptedAt
      }
    }
  }
`;

export interface GetArrivalOffersResponse {
  sources: {
    source: {
      id: string;
      sourceKind: string;
      sourceLabel: string;
      biographyText?: string | null;
      uri?: string | null;
      publication?: { year?: number | null; workType?: string | null; venue?: string | null; authors: string[] } | null;
      credit?: {
        department: string;
        roles: string[];
        endYear?: number | null;
        episodeCount?: number | null;
        creditedAs?: string | null;
      } | null;
      record?: {
        status: string;
        offer?: { offeredBy: string; offeredAt?: string | null; offeredFrom: string[]; acceptedAt?: string | null } | null;
      } | null;
    };
    yields?: {
      claimId: string;
      statement: string;
      claimStatus: string;
      locator?: string | null;
      group?: string | null;
      acceptedAt?: string | null;
    }[];
  }[];
}

/**
 * The owner's decisions on what was offered (CDEV-2304): accept an offered
 * source or set it aside (retract), and the same for each claim an offered
 * bio yielded. The owner's own only; the server refuses anyone else, and the
 * client refuses every write under a person preview.
 */
export const ACCEPT_OFFERED_SOURCE = `
  mutation AcceptOfferedSource($id: ID!) {
    acceptSource(id: $id) {
      alreadyAccepted
    }
  }
`;

export const SET_ASIDE_OFFERED_SOURCE = `
  mutation SetAsideOfferedSource($id: ID!, $reason: String) {
    retractSource(id: $id, reason: $reason) {
      alreadyRetracted
    }
  }
`;

export const ACCEPT_OFFERED_CLAIM = `
  mutation AcceptOfferedClaim($id: ID!) {
    acceptOfferedClaim(id: $id) {
      already
      promotion {
        success
        message
      }
    }
  }
`;

export const SET_ASIDE_OFFERED_CLAIM = `
  mutation SetAsideOfferedClaim($id: ID!, $reason: String) {
    retractOfferedClaim(id: $id, reason: $reason) {
      already
    }
  }
`;

export interface AcceptOfferedClaimResponse {
  acceptOfferedClaim: { already: boolean; promotion?: { success: boolean; message: string } | null };
}

/** The caller's invitation records, oldest first. */
export const GET_MY_INVITATIONS = `
  query GetMyInvitations {
    myInvitations {
      iri
      inviter
      invitee
      locus
      kind
      firstReading
      grants
      invitedAt
    }
  }
`;

/** The caller's current associations, oldest first. */
export const GET_MY_ASSOCIATIONS = `
  query GetMyAssociations {
    myAssociations {
      ${ASSOCIATION_FIELDS}
    }
  }
`;

// ===== Names for the Arrival view: one small read per name, so one refusal costs one label =====

export interface ArrivalPrincipalNameResponse {
  principal: { id: string; name: string | null } | null;
}

/** An inviter's display name. Needs principal.read; a refusal falls back to the IRI's tail. */
export const ARRIVAL_PRINCIPAL_NAME = `
  query ArrivalPrincipalName($id: ID!) {
    principal(id: $id) {
      id
      name
    }
  }
`;

export interface ArrivalLoomTitleResponse {
  loomDocument: { id: string; slug: string; title: string | null } | null;
}

/** A Loom document's title, by the slug its IRI ends in. */
export const ARRIVAL_LOOM_TITLE = `
  query ArrivalLoomTitle($slug: String!) {
    loomDocument(slug: $slug) {
      id
      slug
      title
    }
  }
`;

export interface ArrivalWorkProjectNameResponse {
  workProject: { id: string; name: string | null } | null;
}

/** A work project's name. */
export const ARRIVAL_WORK_PROJECT_NAME = `
  query ArrivalWorkProjectName($id: ID!) {
    workProject(id: $id) {
      id
      name
    }
  }
`;

// ===== Preview as a person (CDEV-2234, MEMFAB-PERSON-PREVIEW-ADDENDUM) =====

export type PersonPreviewBasis = 'INVITER' | 'LOOM_ADMIN' | 'ROOT';

export interface PersonPreviewGQL {
  actor: string;
  viewer: string;
  viewerName: string | null;
  basis: PersonPreviewBasis;
}

export interface PersonPreviewResponse {
  personPreview: PersonPreviewGQL | null;
}

/** The preview the server authorized for this request; null without one. */
export const GET_PERSON_PREVIEW = `
  query GetPersonPreview {
    personPreview {
      actor
      viewer
      viewerName
      basis
    }
  }
`;

export interface PreviewablePersonGQL {
  principal: string;
  name: string | null;
  /** Shown to an admin, and to an inviter for its own invitees. */
  email: string | null;
  basis: PersonPreviewBasis;
}

export interface PreviewablePeopleResponse {
  previewablePeople: PreviewablePersonGQL[];
}

/** The people the caller invited and may preview as (at most 50). */
export const GET_PREVIEWABLE_PEOPLE = `
  query GetPreviewablePeople {
    previewablePeople {
      principal
      name
      email
      basis
    }
  }
`;

export interface PreviewCandidatesResponse {
  previewCandidates: PreviewablePersonGQL[];
}

/**
 * Search the people the caller may preview as (PP-10): a name or email
 * substring (at least 2 characters), or a principal IRI in any form. An admin
 * searches everyone; anyone else only the people it invited.
 */
export const SEARCH_PREVIEW_CANDIDATES = `
  query SearchPreviewCandidates($search: String!, $first: Int) {
    previewCandidates(search: $search, first: $first) {
      principal
      name
      email
      basis
    }
  }
`;
